import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { Plus, Zap, Circle, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { useNavigationHistory } from '@/hooks/useNavigationHistory';

interface EnergyLog {
  id: string;
  date: string;
  created_at: string;
  energy_level: number;
  chakra_states: { name: string; state: string; notes: string }[];
  aura_observations: string;
  blockages_noted: string;
  clearing_methods_used: string[];
  notes: string;
  energy_quality: string;
}

const chakras = [
  { name: 'Root', color: 'bg-red-500' },
  { name: 'Sacral', color: 'bg-orange-500' },
  { name: 'Solar Plexus', color: 'bg-yellow-500' },
  { name: 'Heart', color: 'bg-green-500' },
  { name: 'Throat', color: 'bg-blue-500' },
  { name: 'Third Eye', color: 'bg-indigo-500' },
  { name: 'Crown', color: 'bg-purple-500' }
];

export default function EnergyBodyTracker() {
  const { goBack, getBackButtonLabel } = useNavigationHistory();
  const [logs, setLogs] = useState<EnergyLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    energy_level: 5,
    chakra_states: chakras.map(c => ({ name: c.name, state: 'balanced', notes: '' })),
    aura_observations: '',
    blockages_noted: '',
    clearing_methods_used: [] as string[],
    notes: '',
    energy_quality: 'neutral'
  });

  useEffect(() => {
    loadLogs();
  }, []);

  const loadLogs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { data, error } = await supabase
      .from('energy_body_logs')
      .select('*')
      .eq('user_id', user.id)
      .order('date', { ascending: false });

    if (error) {
      toast({ title: 'Error loading logs', variant: 'destructive' });
    } else {
      setLogs((data as any) || []);
    }
    setLoading(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    const { error } = await supabase
      .from('energy_body_logs')
      .insert({
        user_id: user.id,
        date: new Date().toISOString().split('T')[0],
        ...formData
      });

    if (error) {
      toast({ title: 'Error saving log', variant: 'destructive' });
    } else {
      toast({ title: 'Energy log saved' });
      setShowForm(false);
      loadLogs();
    }
  };

  if (loading) {
    return <div className="text-center py-8">Loading energy logs...</div>;
  }

  return (
    <div className="container mx-auto p-4 max-w-4xl space-y-6">
      <Button 
        onClick={goBack}
        variant="ghost" 
        className="mb-4"
      >
        <ArrowLeft className="mr-2 h-4 w-4" />
        {getBackButtonLabel()}
      </Button>
      
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary animate-pulse" />
            Energy Body Tracker
          </CardTitle>
          <CardDescription>
            Monitor your subtle energy system, chakra states, and aura quality. Track how energy work and clearing practices affect your overall energetic health and psychic receptivity.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={() => setShowForm(!showForm)} className="mb-4">
            <Plus className="h-4 w-4 mr-2" />
            Log Energy State
          </Button>

          {showForm && (
            <form onSubmit={handleSubmit} className="space-y-6 mb-6 p-4 border border-border rounded-lg bg-card animate-fade-in">
              <div className="space-y-2">
                <Label>Overall Energy Level: {formData.energy_level}/10</Label>
                <Slider
                  value={[formData.energy_level]}
                  onValueChange={([value]) => setFormData({ ...formData, energy_level: value })}
                  min={1}
                  max={10}
                  step={1}
                  className="w-full"
                />
              </div>

              <div className="space-y-3">
                <Label>Chakra States</Label>
                {chakras.map((chakra, idx) => (
                  <div key={chakra.name} className="flex items-center gap-3">
                    <Circle className={`h-6 w-6 ${chakra.color} rounded-full animate-pulse`} />
                    <span className="w-28 text-sm font-light">{chakra.name}</span>
                    <select
                      className="flex-1 px-3 py-2 rounded-md border bg-background"
                      value={formData.chakra_states[idx].state}
                      onChange={(e) => {
                        const newStates = [...formData.chakra_states];
                        newStates[idx].state = e.target.value;
                        setFormData({ ...formData, chakra_states: newStates });
                      }}
                    >
                      <option value="balanced">Balanced</option>
                      <option value="overactive">Overactive</option>
                      <option value="underactive">Underactive</option>
                      <option value="blocked">Blocked</option>
                    </select>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label>Aura Observations</Label>
                <Textarea
                  value={formData.aura_observations}
                  onChange={(e) => setFormData({ ...formData, aura_observations: e.target.value })}
                  placeholder="Colors, textures, density, clarity observed in your auric field..."
                  rows={3}
                />
              </div>

              <div className="space-y-2">
                <Label>Blockages or Disturbances</Label>
                <Textarea
                  value={formData.blockages_noted}
                  onChange={(e) => setFormData({ ...formData, blockages_noted: e.target.value })}
                  placeholder="Any stuck energy, cold spots, or areas of concern..."
                  rows={2}
                />
              </div>

              <div className="space-y-2">
                <Label>Additional Notes</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="General observations, feelings, or insights..."
                  rows={2}
                />
              </div>

              <div className="flex gap-2">
                <Button type="submit">Save Log</Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
              </div>
            </form>
          )}

          <div className="space-y-4">
            {logs.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">
                Start tracking your energy body to identify patterns in your subtle system.
              </p>
            ) : (
              logs.map((log) => (
                <Card key={log.id} className="bg-muted/30 animate-fade-in">
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <Badge variant="outline">Energy: {log.energy_level}/10</Badge>
                      <span className="text-sm text-muted-foreground">
                        {format(new Date(log.date), 'PPP')}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {log.aura_observations && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Aura:</p>
                        <p className="text-sm text-muted-foreground font-light">{log.aura_observations}</p>
                      </div>
                    )}
                    {log.blockages_noted && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Blockages:</p>
                        <p className="text-sm text-muted-foreground font-light">{log.blockages_noted}</p>
                      </div>
                    )}
                    {log.notes && (
                      <div>
                        <p className="text-sm font-light text-primary mb-1">Notes:</p>
                        <p className="text-sm text-muted-foreground font-light">{log.notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
